import type { FieldConfig, LeaseDetailsFormConfig } from './config';

export type LeaseDetailsFormValues = Record<string, unknown>;

const APPLICANTS_KEY = 'applicants';
const FLAT_SECTION_KEYS = ['leaseInformation', 'screening', 'coverage'] as const;

// Readonly fields seed `field.value` (so submit payloads include the displayed
// value); everything else falls back to `defaultValue ?? null`.
export const collectFieldDefaults = (
  fields: Record<string, FieldConfig>,
): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {};
  for (const [name, field] of Object.entries(fields)) {
    defaults[name] = field.kind === 'text-readonly' ? field.value : (field.defaultValue ?? null);
  }
  return defaults;
};

// Field names across the three flat sections share one RHF form-state object
// (the engine doesn't namespace by section). Collisions would cause the later
// spread to silently overwrite the earlier — both fields would render but
// share state. Fail fast with a clear message instead.
const assertNoFieldCollisions = (config: LeaseDetailsFormConfig): void => {
  const ownerBySectionFieldName = new Map<string, (typeof FLAT_SECTION_KEYS)[number]>();
  for (const sectionKey of FLAT_SECTION_KEYS) {
    for (const name of Object.keys(config.sections[sectionKey].fields)) {
      if (name === APPLICANTS_KEY) {
        throw new Error(
          `LeaseDetailsForm: field name '${APPLICANTS_KEY}' is reserved for the applicants array (collided in section '${sectionKey}').`,
        );
      }
      const existing = ownerBySectionFieldName.get(name);
      if (existing) {
        throw new Error(
          `LeaseDetailsForm: field name '${name}' is declared in both '${existing}' and '${sectionKey}'. Field names must be unique across leaseInformation, screening, and coverage — they share a single flat form-state object.`,
        );
      }
      ownerBySectionFieldName.set(name, sectionKey);
    }
  }
};

// Applicants is handled separately from scalar fields: the array is padded up
// to `applicantsConfig.min` so the form always mounts at or above the floor,
// regardless of what `initialValues` provides.
export const buildInitialFormValues = (
  config: LeaseDetailsFormConfig,
  initialValues?: Partial<LeaseDetailsFormValues>,
): LeaseDetailsFormValues => {
  assertNoFieldCollisions(config);

  // Separate `applicants` so it doesn't get clobbered by the top-level spread —
  // we need to merge it with `min`-floor seeding, not replace it.
  const { applicants: providedApplicants, ...flatInitial } = initialValues ?? {};

  const defaults: LeaseDetailsFormValues = {
    ...collectFieldDefaults(config.sections.leaseInformation.fields),
    ...collectFieldDefaults(config.sections.screening.fields),
    ...collectFieldDefaults(config.sections.coverage.fields),
    ...flatInitial,
  };

  const applicantsConfig = config.sections.applicants;
  const provided = Array.isArray(providedApplicants) ? [...providedApplicants] : [];
  while (provided.length < applicantsConfig.min) {
    provided.push({
      ...applicantsConfig.defaultEntry,
      ...collectFieldDefaults(applicantsConfig.fields),
    });
  }
  defaults[APPLICANTS_KEY] = provided;

  return defaults;
};
